import { ExerciseFilter } from '@helpers/types/exerciseFilter';
import {
  ExerciseSorting,
  ExerciseSortingCriteria,
  ExerciseSortingDirection,
  loadExerciseSorting,
  saveExerciseSorting,
} from '@helpers/types/exerciseSorting';
import { useAuthStore } from '@stores/authStore';
import { useQueryClient } from '@tanstack/vue-query';
import {
  exerciseFavouritesQueries,
  exerciseFavouritesKeys,
  exerciseFavouritesMutations,
  exercisesQueries,
  exercisesKeys,
} from '@therame/api-client';
import {
  Difficulty,
  ExerciseFullSizeDto,
  TransformerWrapper,
  UpdateExerciseFavouriteDto,
  Visibility,
} from '@therame/shared';
import { defineStore } from 'pinia';
import { Subject } from 'rxjs';
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';

type ExerciseLibraryStore = {
  exercises: Ref<ExerciseFullSizeDto[]>;
  exerciseFilter: Ref<ExerciseFilter>;
  exerciseSorting: Ref<ExerciseSorting>;
  loadAllExercises: () => Promise<void>;
  addFavourite: (exercise: ExerciseFullSizeDto) => Promise<void>;
  removeFavourite: (exercise: ExerciseFullSizeDto) => Promise<void>;
  isFilterActive: ComputedRef<boolean>;
  isFavourite: ComputedRef<(video: ExerciseFullSizeDto) => boolean>;
  favourites: Ref<Set<string>>;
  exerciseFilterActive: ComputedRef<boolean>;
  ownExercises: ComputedRef<ExerciseFullSizeDto[]>;
  officeExercises: ComputedRef<ExerciseFullSizeDto[]>;
  resetExerciseFilter: () => void;
  backToCategories: () => void;
  resetExerciseSorting: () => void;
  sortedAndFilteredExercises: ComputedRef<ExerciseFullSizeDto[]>;
  ownExercisesCount: ComputedRef<number>;
  officeExercisesCount: ComputedRef<number>;
  exerciseSearchBarSelectTrigger: Subject<ExerciseFullSizeDto>;
  addExerciseModalPreselectedVisibility: Ref<Visibility | undefined>;
  updateExerciseFavourites: () => Promise<void>;
};

export const useExerciseLibraryStore = defineStore('exerciseLibrary', (): ExerciseLibraryStore => {
  const authStore = useAuthStore();
  const queryClient = useQueryClient();

  const favourites = ref<Set<string>>(new Set<string>());
  const exercises = ref<ExerciseFullSizeDto[]>([]);
  const exerciseFilter = ref<ExerciseFilter>(new ExerciseFilter());
  // Restored from localStorage and written back on every change, so the order
  // the user picked is still there after a reload. `deep`, because the sorting
  // menu mutates `criteria`/`direction` in place rather than replacing the ref.
  const exerciseSorting = ref<ExerciseSorting>(loadExerciseSorting());

  watch(exerciseSorting, (value) => saveExerciseSorting(value), { deep: true });
  const exerciseSearchBarSelectTrigger: Subject<ExerciseFullSizeDto> =
    new Subject<ExerciseFullSizeDto>();

  const addExerciseModalPreselectedVisibility = ref<Visibility | undefined>(undefined);

  const exerciseFilterActive = computed(() => {
    return !!exerciseFilter.value.exercisePackage || exerciseFilter.value.searchText.length > 0;
  });

  const isFilterActive = computed(() => {
    const isActive = exerciseFilter.value;

    return (
      isActive.bodyRegionIds.size > 0 ||
      isActive.difficulties.size > 0 ||
      isActive.searchText.trim().length > 0 ||
      isActive.onlyFavourites ||
      isActive.onlyOwnExercises ||
      isActive.onlyOfficeExercises ||
      isActive.onlyWithDevice ||
      isActive.onlyWithoutDevice
    );
  });

  const ownExercisesCount = computed(() => {
    return exercises.value.filter(
      (exercise) =>
        exercise.uploadedById === authStore.authContext?.user?.id &&
        (exercise.visibility === Visibility.ONE_OFFICE ||
          exercise.visibility === Visibility.ONE_THERAPIST),
    ).length;
  });

  const officeExercisesCount = computed(() => {
    return exercises.value.filter((exercise) => exercise.visibility === Visibility.ONE_OFFICE)
      .length;
  });

  const resetExerciseFilter = () => {
    exerciseFilter.value.searchText = '';
    exerciseFilter.value = new ExerciseFilter();
  };

  // Leaves the body-region, device and difficulty picks in place: only the category
  // and the search text decide whether the grid or the package overview is shown.
  const backToCategories = () => {
    exerciseFilter.value.exercisePackage = undefined;
    exerciseFilter.value.searchText = '';
  };

  const resetExerciseSorting = () => {
    exerciseSorting.value.criteria = ExerciseSortingCriteria.ALPHABET;
    exerciseSorting.value.direction = ExerciseSortingDirection.LOW_TO_HIGH;
  };

  const filteredExercises = computed(() => {
    return exercises.value.filter((exercise) => {
      const bodyRegionMatches: boolean =
        exerciseFilter.value.bodyRegionIds.size === 0 ||
        exercise.bodyRegions
          .map((bodyRegion) => bodyRegion.id)
          .find((bodyRegionId) => exerciseFilter.value.bodyRegionIds.has(bodyRegionId)) !==
          undefined;

      const exercisePackageMatches: boolean =
        !exerciseFilter.value.exercisePackage ||
        exercise.exercisePackages
          .map((exercisePackage) => exercisePackage.id)
          .find(
            (exercisePackageId) => exerciseFilter.value.exercisePackage?.id === exercisePackageId,
          ) !== undefined;

      const difficultyMatches =
        exerciseFilter.value.difficulties.size === 0 ||
        exerciseFilter.value.difficulties.has(exercise.difficulty);

      const searchTextMatches =
        exerciseFilter.value.searchText.length === 0 ||
        exercise.title
          ?.toLocaleLowerCase()
          .includes(exerciseFilter.value.searchText.toLocaleLowerCase());

      const onlyFavouritesMatches =
        !exerciseFilter.value.onlyFavourites ||
        (exerciseFilter.value.onlyFavourites && favourites.value.has(exercise.id));

      const onlyOwnExercises =
        !exerciseFilter.value.onlyOwnExercises ||
        (exerciseFilter.value.onlyOwnExercises &&
          exercise.uploadedById === authStore.authContext?.user?.id &&
          (exercise.visibility === Visibility.ONE_THERAPIST ||
            exercise.visibility === Visibility.ONE_OFFICE));

      const onlyOfficeExercises =
        !exerciseFilter.value.onlyOfficeExercises ||
        (exerciseFilter.value.onlyOfficeExercises && exercise.visibility === Visibility.ONE_OFFICE);

      const onlyWithDevice =
        !exerciseFilter.value.onlyWithDevice ||
        (exerciseFilter.value.onlyWithDevice && (exercise.deviceText?.length || 0) > 0);

      const onlyWithoutDevice =
        !exerciseFilter.value.onlyWithoutDevice ||
        (exerciseFilter.value.onlyWithoutDevice && !exercise.deviceText);

      return (
        bodyRegionMatches &&
        exercisePackageMatches &&
        difficultyMatches &&
        searchTextMatches &&
        onlyFavouritesMatches &&
        onlyOwnExercises &&
        onlyOfficeExercises &&
        onlyWithDevice &&
        onlyWithoutDevice
      );
    });
  });

  const sortedAndFilteredExercises = computed(() => {
    const list = filteredExercises.value
      // This filtering is necessary for updating the exercise grid.
      // If removed the updated sorted array is not shown.
      // TODO: Find better resolution
      .filter((exercise) => {
        return exercise.id !== undefined;
      })
      .sort((exerciseA, exerciseB) => {
        if (exerciseSorting.value.criteria === ExerciseSortingCriteria.ALPHABET) {
          const sort =
            exerciseSorting.value.direction === ExerciseSortingDirection.LOW_TO_HIGH
              ? (exerciseA.title + '').localeCompare(exerciseB.title + '')
              : (exerciseB.title + '').localeCompare(exerciseA.title + '');
          return sort;
        }

        if (exerciseSorting.value.criteria === ExerciseSortingCriteria.DIFFICULTY) {
          const difficultyA = getDifficultyRating(exerciseA.difficulty);
          const difficultyB = getDifficultyRating(exerciseB.difficulty);
          return exerciseSorting.value.direction === ExerciseSortingDirection.LOW_TO_HIGH
            ? difficultyA - difficultyB
            : difficultyB - difficultyA;
        }

        return 0;
      });
    return list;
  });

  const getDifficultyRating = (difficulty: Difficulty) => {
    switch (difficulty) {
      case Difficulty.EASY:
        return 1;
      case Difficulty.MEDIUM:
        return 2;
      case Difficulty.HARD:
        return 3;
    }
  };

  const loadAllExercises = async () => {
    const userId = authStore.authContext?.user?.id;
    if (!userId) return;

    const exerciseDtos = await queryClient.fetchQuery({
      queryKey: exercisesKeys.byUser(userId),
      queryFn: () => exercisesQueries.getByUser(userId),
      staleTime: 30_000,
    });
    exercises.value = exerciseDtos.map((exerciseDto) =>
      TransformerWrapper.plainToInstance(ExerciseFullSizeDto, exerciseDto, {
        groups: [authStore.authContext?.user?.role || ''],
      }),
    );
  };

  const ownExercises = computed(() => {
    return exercises.value.filter((exercise) => exercise.visibility === Visibility.ONE_THERAPIST);
  });

  const officeExercises = computed(() => {
    return exercises.value.filter((exercise) => exercise.visibility === Visibility.ONE_OFFICE);
  });

  const addFavourite = async (exercise: ExerciseFullSizeDto) => {
    const exerciseFavouriteDto = new UpdateExerciseFavouriteDto();
    exerciseFavouriteDto.exerciseId = exercise.id;
    exerciseFavouriteDto.userId = authStore.authContext?.user?.id || '';
    await exerciseFavouritesMutations.add(exerciseFavouriteDto);
    favourites.value.add(exercise.id);
  };

  const removeFavourite = async (exercise: ExerciseFullSizeDto) => {
    const exerciseFavouriteDto = new UpdateExerciseFavouriteDto();
    exerciseFavouriteDto.exerciseId = exercise.id;
    exerciseFavouriteDto.userId = authStore.authContext?.user?.id || '';
    await exerciseFavouritesMutations.remove(exerciseFavouriteDto);
    favourites.value.delete(exercise.id);
  };

  const isFavourite = computed(() => (video: ExerciseFullSizeDto) => {
    return favourites.value.has(video.id);
  });

  const updateExerciseFavourites = async () => {
    const userId = authStore.authContext?.user?.id;
    if (!userId) {
      favourites.value = new Set();
      return;
    }

    const res = await queryClient.fetchQuery({
      queryKey: exerciseFavouritesKeys.byUser(userId),
      queryFn: () => exerciseFavouritesQueries.getByUser(userId),
      staleTime: 30_000,
    });
    favourites.value = new Set(res.map((fav) => fav.exerciseId));
  };

  const store: ExerciseLibraryStore = {
    exercises,
    exerciseFilter,
    exerciseSorting,
    loadAllExercises,
    addFavourite,
    removeFavourite,
    isFilterActive,
    isFavourite,
    favourites,
    exerciseFilterActive,
    ownExercises,
    officeExercises,
    resetExerciseFilter,
    backToCategories,
    resetExerciseSorting,
    sortedAndFilteredExercises,
    ownExercisesCount,
    officeExercisesCount,
    exerciseSearchBarSelectTrigger,
    addExerciseModalPreselectedVisibility,
    updateExerciseFavourites,
  };
  return store;
});
